import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '../../environments/environment';
import { catchError, throwError } from 'rxjs';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const documentRef = inject(DOCUMENT);
    let request: typeof req;

    const unsafeMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];
    const isUnsafeMethod = unsafeMethods.includes(req.method.toUpperCase());
    const xsrfToken = getCookieValue(documentRef, 'XSRF-TOKEN');
    const xsrfHeaders =
        isUnsafeMethod && xsrfToken && !req.headers.has('X-XSRF-TOKEN')
            ? { 'X-XSRF-TOKEN': xsrfToken }
            : {};

    if (req.url === environment.userinfo) {
        request = req.clone({
            headers: req.headers.set('scope', 'openid profile email')
                .set('x-encrypted', 'true')
                .set('x-refresh', 'true')
                .set('x-kcv', 'identity')
                .set('Ocp-Apim-Subscription-Key', environment.userInfoSubscriptionKey)
                .set('X-XSRF-TOKEN', xsrfHeaders['X-XSRF-TOKEN'] || req.headers.get('X-XSRF-TOKEN') || '')
        })

    } else if (req.url === environment.shgUrl) {
        request = req.clone({
            headers: req.headers.set('x-kcv', 'secure-identity')
                .set('Ocp-Apim-Subscription-Key', environment.userInfoSubscriptionKey)
                .set('X-XSRF-TOKEN', xsrfHeaders['X-XSRF-TOKEN'] || req.headers.get('X-XSRF-TOKEN') || '')
        })
    } else if (req.url.includes(environment.permission)) {
        request = req.clone({
            headers: req.headers
                .set('Ocp-Apim-Subscription-Key', environment.permissionSubscriptionKey)
                .set('X-XSRF-TOKEN', xsrfHeaders['X-XSRF-TOKEN'] || req.headers.get('X-XSRF-TOKEN') || '')
        })
    } else if (req.url.includes(environment.keycloakImageUrl)) {
        request = req.clone({
            headers: req.headers
                .set('Ocp-Apim-Subscription-Key', environment.permissionSubscriptionKey)
                .set('X-XSRF-TOKEN', xsrfHeaders['X-XSRF-TOKEN'] || req.headers.get('X-XSRF-TOKEN') || '')
        })
    } else if (environment.reportingBaseUri && req.url.includes(environment.reportingBaseUri)) {
        request = req.clone({
            headers: req.headers
                .set('x-kcv', environment.reportingKcv ?? 'secure-identity')
                .set(
                    'Ocp-Apim-Subscription-Key',
                    firstNonEmpty(environment.reportingSubscriptionKey, environment.subscriptionKey)
                )
                .set('X-XSRF-TOKEN', xsrfHeaders['X-XSRF-TOKEN'] || req.headers.get('X-XSRF-TOKEN') || '')
                .set('x-refresh','true')
        })
    } else if (req.url.includes(environment.downloadUrl)) {
        request = req.clone({
            headers: req.headers
                .set('x-encrypted', 'false')
                .set('x-kcv', 'identity')
                .set('Ocp-Apim-Subscription-Key', environment.subscriptionKey)
                .set('X-XSRF-TOKEN', xsrfHeaders['X-XSRF-TOKEN'] || req.headers.get('X-XSRF-TOKEN') || '')
        })
    } else {
        request = req.clone({
            headers: req.headers.set('Ocp-Apim-Subscription-Key', environment.subscriptionKey)
                .set('X-XSRF-TOKEN', xsrfHeaders['X-XSRF-TOKEN'] || req.headers.get('X-XSRF-TOKEN') || '')
        })
    }

    request = stripEmptyApimSubscriptionKey(request);

    if (!xsrfHeaders['X-XSRF-TOKEN']) {
        request = req.clone({
            headers: request.headers.delete('X-XSRF-TOKEN')
        });
    }

    return next(request).pipe(
        catchError((error: unknown) => {
            if (error instanceof HttpErrorResponse) {
                return throwError(() => sanitizeHttpError(error));
            }

            return throwError(() => new HttpErrorResponse({
                status: 0,
                statusText: 'Request failed',
                url: request.url,
                error: { message: 'Something went wrong. Please try again.' }
            }));
        })
    );
};

function stripEmptyApimSubscriptionKey<T>(request: HttpRequest<T>): HttpRequest<T> {
    const subscriptionKey = request.headers.get('Ocp-Apim-Subscription-Key')?.trim();
    if (subscriptionKey) {
        return request;
    }

    return request.clone({
        headers: request.headers.delete('Ocp-Apim-Subscription-Key')
    });
}

function firstNonEmpty(...values: Array<string | undefined>): string {
    return values.find((value: string | undefined) => !!value?.trim()) ?? '';
}

function getCookieValue(documentRef: Document, name: string): string {
    const cookie = documentRef.cookie
        .split(';')
        .map((item: string) => item.trim())
        .find((item: string) => item.startsWith(`${name}=`));

    return cookie ? decodeURIComponent(cookie.split('=').slice(1).join('=')) : '';
}

function sanitizeHttpError(error: HttpErrorResponse): HttpErrorResponse {
    const status = error.status || 0;
    const safeMessage =
        status >= 500
            ? 'Something went wrong on the server. Please try again later.'
            : status === 401
                ? 'Your session has expired. Please sign in again.'
                : status === 403
                    ? 'You are not authorized to perform this action.'
                    : status === 404
                        ? 'The requested resource was not found.'
                        : status === 400
                            ? 'The request could not be processed.'
                            : 'Something went wrong. Please try again.';

    return new HttpErrorResponse({
        status,
        statusText: error.statusText || 'Request failed',
        url: error.url || undefined,
        headers: error.headers,
        error: {
            message: safeMessage
        }
    });
}
